import { CommonModule, Location } from '@angular/common';
import { HttpClient, HttpContext, HttpHeaders } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { BehaviorSubject, catchError, combineLatest, finalize, map, of, shareReplay, switchMap } from 'rxjs';
import { Bundle, Composition, Organization, Patient, ServiceRequest } from 'fhir/r4';
import { backendEndPointToken, terminologyCanonicalUrlBaseToken } from '../app.config';
import { ReferralViewerSourceService } from '../referral/services/referral-viewer-source.service';
import { ReferredPatientShellInput, ReferredPatientShellService } from '../referral/services/referred-patient-shell.service';
import { AuthService } from '../shared/auth/auth.service';
import { SuccessToastService } from '../shared/success-toast.service';
import { ErrorService } from '../shared/error.service';
import { ReferralDocumentViewerComponent } from './referral-document-viewer.component';
import { DisableCountParam, DisableSortParam, LoadingUIEnabled } from '../loading.interceptor';

@Component({
    selector: 'app-request-document-viewer-page',
    standalone: true,
    imports: [
        CommonModule,
        RouterLink,
        MatButtonModule,
        MatCardModule,
        MatIconModule,
        MatProgressSpinnerModule,
        ReferralDocumentViewerComponent
    ],
    templateUrl: './request-document-viewer-page.component.html',
    styleUrl: './request-document-viewer-page.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class RequestDocumentViewerPageComponent {
    private readonly route = inject(ActivatedRoute);
    private readonly location = inject(Location);
    private readonly http = inject(HttpClient);
    private readonly backendUrl = inject(backendEndPointToken);
    private readonly terminologyBaseUrl = inject(terminologyCanonicalUrlBaseToken);
    private readonly shellBuilder = inject(ReferredPatientShellService);
    private readonly successToast = inject(SuccessToastService);
    private readonly errorService = inject(ErrorService);
    private readonly auth = inject(AuthService);
    private readonly referralViewerSourceService = inject(ReferralViewerSourceService);
    private readonly createdPatientIdSubject = new BehaviorSubject<string | null>(null);
    private readonly referralIdentifierSystem = `${this.terminologyBaseUrl}/NamingSystem/referral-identifier`;

    readonly serviceRequestId$ = this.route.paramMap.pipe(map(params => params.get('serviceRequestId')?.trim() || ''));
    readonly referralBundle$ = this.serviceRequestId$.pipe(
        switchMap(serviceRequestId => this.referralViewerSourceService.loadBundleForServiceRequestId(serviceRequestId))
    );
    readonly existingLocalPatientId$ = combineLatest([
        this.referralBundle$,
        this.serviceRequestId$
    ]).pipe(
        switchMap(([bundle, serviceRequestId]) => this.findExistingLocalPatientId(bundle, serviceRequestId)),
        shareReplay({ bufferSize: 1, refCount: true })
    );
    readonly patientId$ = combineLatest([
        this.existingLocalPatientId$,
        this.createdPatientIdSubject.asObservable()
    ]).pipe(
        map(([existingId, createdId]) => createdId?.trim() || existingId?.trim() || '')
    );
    // Derive every UI-ready slice from a single stream so the template only binds once.
    readonly viewModel$ = combineLatest([
        this.referralBundle$,
        this.patientId$,
        this.serviceRequestId$,
        this.existingLocalPatientId$
    ]).pipe(
        map(([bundle, patientId, serviceRequestId, existingLocalPatientId]) =>
            this.buildViewModel(bundle, patientId, serviceRequestId, existingLocalPatientId)
        ),
        shareReplay({ bufferSize: 1, refCount: true })
    );
    creatingShell = false;
    shellCreationError: string | null = null;

    back(): void {
        this.location.back();
    }

    patientSummaryLink(patientId: string): string[] {
        return ['/app', 'patients', patientId, 'summary'];
    }

    createDestinationPatientShell(viewModel: RequestDocumentViewerViewModel): void {
        if (!viewModel.shellInput || this.creatingShell) {
            return;
        }

        this.shellCreationError = null;
        this.creatingShell = true;

        const artifacts = this.shellBuilder.buildArtifacts(viewModel.shellInput);
        this.http.post(`${this.backendUrl}`, artifacts.transactionBundle, {
            headers: new HttpHeaders({
                'Content-Type': 'application/fhir+json',
                Accept: 'application/fhir+json'
            })
        }).pipe(
            finalize(() => {
                this.creatingShell = false;
            })
        ).subscribe({
            next: response => {
                const patientId = this.extractCreatedPatientId(response);
                if (patientId) {
                    this.createdPatientIdSubject.next(patientId);
                }

                const patientLabel = viewModel.patientDisplay || 'patient';
                this.successToast.success(`${patientLabel} is now available in your roster.`);
            },
            error: () => {
                this.shellCreationError = 'We could not create the destination patient shell. Please try again.';
                this.errorService.openandCloseError('Unable to create the destination patient shell.');
            }
        });
    }

    private buildViewModel(
        bundle: Bundle | null,
        patientId: string,
        serviceRequestId: string,
        existingLocalPatientId: string
    ): RequestDocumentViewerViewModel {
        const patient = this.extractResource<Patient>(bundle, 'Patient');
        const patientDisplay = this.displayPatientName(patient);
        const shellPlan = this.buildShellPlan(bundle, patient, serviceRequestId, patientId);

        return {
            bundle,
            patientId,
            patientDisplay,
            hasLocalPatientShell: !!patientId,
            existingLocalPatientId: existingLocalPatientId || null,
            referralIdentifier: shellPlan.referralIdentifier,
            destinationOrganizationDisplay: shellPlan.destinationDisplay ?? undefined,
            shellInput: shellPlan.input,
            shellDisabledReason: shellPlan.reason,
            canCreatePatientShell: !!shellPlan.input
        };
    }

    private buildShellPlan(
        bundle: Bundle | null,
        patient: Patient | undefined,
        serviceRequestId: string,
        localPatientId: string
    ): ShellPlan {
        if (!bundle) {
            return { input: null, referralIdentifier: null, destinationDisplay: null, reason: 'Document bundle is still loading.' };
        }

        if (!patient) {
            return { input: null, referralIdentifier: null, destinationDisplay: null, reason: 'The shared bundle does not include a Patient resource.' };
        }

        const destinationOrg = this.getDestinationOrganization();
        if (!destinationOrg.id) {
            return { input: null, referralIdentifier: null, destinationDisplay: null, reason: 'Your account is missing an organization identifier.' };
        }

        const referralIdentifier = this.extractReferralIdentifier(bundle) || serviceRequestId || this.makeFallbackIdentifier();
        const sourceOrg = this.extractSourceOrganization(bundle);

        if (localPatientId) {
            return {
                input: null,
                referralIdentifier,
                destinationDisplay: destinationOrg.display,
                reason: 'A local patient shell already exists for this referral.'
            };
        }

        return {
            input: {
                sourcePatient: patient,
                destinationOrganizationId: destinationOrg.id,
                destinationOrganizationDisplay: destinationOrg.display,
                sourceOrganizationId: sourceOrg.id,
                sourceOrganizationDisplay: sourceOrg.display,
                referralIdentifier
            },
            referralIdentifier,
            destinationDisplay: destinationOrg.display,
            reason: null
        };
    }

    private findExistingLocalPatientId(bundle: Bundle | null, serviceRequestId: string) {
        const referralIdentifier = this.extractReferralIdentifier(bundle) || serviceRequestId || '';
        if (!referralIdentifier) {
            return of('');
        }

        const destinationOrg = this.getDestinationOrganization();
        if (!destinationOrg.id) {
            return of('');
        }

        const context = new HttpContext()
            .set(LoadingUIEnabled, false)
            .set(DisableSortParam, true)
            .set(DisableCountParam, true);
        const identifierValue = `${this.referralIdentifierSystem}|${referralIdentifier}`;
        const url = `${this.backendUrl}/Patient?identifier=${encodeURIComponent(identifierValue)}&_count=20`;

        return this.http.get<Bundle>(url, { context }).pipe(
            map((responseBundle) => this.extractExistingLocalPatientId(responseBundle, referralIdentifier)),
            catchError(() => of(''))
        );
    }

    private extractExistingLocalPatientId(bundle: Bundle | null, referralIdentifier: string): string {
        const entries = Array.isArray(bundle?.entry) ? bundle.entry : [];
        const match = entries
            .map(entry => entry.resource as Patient | undefined)
            .find((resource) => resource?.resourceType === 'Patient' && this.patientHasReferralIdentifier(resource, referralIdentifier));

        return match?.id?.trim() || '';
    }

    private patientHasReferralIdentifier(patient: Patient | undefined, referralIdentifier: string): boolean {
        if (!patient || !referralIdentifier) {
            return false;
        }

        return (patient.identifier ?? []).some(identifier =>
            this.isReferralIdentifier(identifier.system) && (identifier.value ?? '').trim() === referralIdentifier
        );
    }

    private getDestinationOrganization(): { id: string | null; display?: string } {
        const currentUser = this.auth.user.getValue();
        const id = currentUser?.organizationId?.trim()
            || currentUser?.hospitalId?.trim()
            || null;

        const display = currentUser?.['organizationName']?.trim()
            || currentUser?.['hospitalName']?.trim()
            || currentUser?.display?.trim()
            || currentUser?.name?.trim()
            || undefined;

        return { id, display };
    }

    private extractSourceOrganization(bundle: Bundle | null): { id?: string; display?: string } {
        const composition = this.extractResource<Composition>(bundle, 'Composition');
        const compositionOrgRef = composition?.custodian?.reference;
        const organizationFromBundle = this.extractResourceByReference<Organization>(bundle, compositionOrgRef)
            || this.extractResource<Organization>(bundle, 'Organization');

        return {
            id: this.extractIdFromReference(compositionOrgRef) || organizationFromBundle?.id,
            display: composition?.custodian?.display || organizationFromBundle?.name
        };
    }

    private extractReferralIdentifier(bundle: Bundle | null): string | null {
        const serviceRequest = this.extractResource<ServiceRequest>(bundle, 'ServiceRequest');
        if (!serviceRequest) {
            return null;
        }

        const identifierValue = serviceRequest.identifier?.find(identifier => this.isReferralIdentifier(identifier.system))?.value
            || serviceRequest.requisition?.value
            || serviceRequest.id;

        return identifierValue || null;
    }

    private extractResource<T>(bundle: Bundle | null, resourceType: string): T | undefined {
        if (!bundle?.entry?.length) {
            return undefined;
        }

        return bundle.entry
            .map(entry => entry.resource)
            .find(resource => resource?.resourceType === resourceType) as T | undefined;
    }

    private extractResourceByReference<T>(bundle: Bundle | null, reference?: string | null): T | undefined {
        const normalizedReference = this.normalizeReference(reference);
        if (!bundle?.entry?.length || !normalizedReference) {
            return undefined;
        }

        return bundle.entry
            .map(entry => entry.resource)
            .find(resource => resource?.id && this.normalizeReference(`${resource.resourceType}/${resource.id}`) === normalizedReference) as T | undefined;
    }

    private extractCreatedPatientId(response: any): string | null {
        if (!response) {
            return null;
        }

        if (response.resourceType === 'Patient' && response.id) {
            return response.id;
        }

        if (response.resourceType === 'Bundle' && Array.isArray(response.entry)) {
            const patientEntry = response.entry.find((entry: any) => {
                const resource = entry.resource;
                if (resource?.resourceType === 'Patient' && resource.id) {
                    return true;
                }

                const location = entry.response?.location;
                return typeof location === 'string' && location.startsWith('Patient/');
            });

            if (patientEntry?.resource?.id) {
                return patientEntry.resource.id;
            }

            const location = patientEntry?.response?.location;
            if (location) {
                return this.extractIdFromReference(location);
            }
        }

        return null;
    }

    private displayPatientName(patient?: Patient): string {
        const name = patient?.name?.[0];
        const given = name?.given?.join(' ') ?? '';
        const family = name?.family ?? '';
        const label = [given, family].filter(Boolean).join(' ').trim();
        return label || patient?.id || 'Referred patient';
    }

    private normalizeReference(reference?: string | null): string | null {
        const trimmed = reference?.trim();
        if (!trimmed) {
            return null;
        }

        const withoutBase = trimmed.includes('/') ? trimmed.split('/').slice(-2).join('/') : trimmed;
        return withoutBase.replace(/^\/+/, '') || null;
    }

    private extractIdFromReference(reference?: string | null): string | null {
        const normalized = this.normalizeReference(reference);
        if (!normalized) {
            return null;
        }

        const parts = normalized.split('/').filter(Boolean);
        if (!parts.length) {
            return null;
        }

        if (parts.length >= 3 && parts[parts.length - 2] === '_history') {
            return parts[parts.length - 3] || null;
        }

        return parts[parts.length - 1] || null;
    }

    private isReferralIdentifier(system?: string | null): boolean {
        return !!system && system.includes('referral-identifier');
    }

    private makeFallbackIdentifier(): string {
        if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
            return crypto.randomUUID();
        }

        return `referral-${Date.now()}`;
    }
}

interface RequestDocumentViewerViewModel {
    bundle: Bundle | null;
    patientId: string;
    patientDisplay: string;
    hasLocalPatientShell: boolean;
    existingLocalPatientId: string | null;
    referralIdentifier: string | null;
    destinationOrganizationDisplay?: string;
    shellInput: ReferredPatientShellInput | null;
    shellDisabledReason: string | null;
    canCreatePatientShell: boolean;
}

interface ShellPlan {
    input: ReferredPatientShellInput | null;
    referralIdentifier: string | null;
    destinationDisplay?: string | null;
    reason: string | null;
}